import React, { useState, useEffect } from "react";
import { connectToContract, contractAddress } from "./CrownContract";
import { FaUserShield, FaGift, FaPlus, FaSignOutAlt } from "react-icons/fa";
import { formatEther, parseEther } from "ethers"; // Upravený import

const ValidatorList = ({ provider, account }) => {
  const [validators, setValidators] = useState([]);
  const [filterAddress, setFilterAddress] = useState("");
  const [validatorNumber, setValidatorNumber] = useState("");
  const [selectedValidator, setSelectedValidator] = useState(null);
  const [isPendingValidator, setIsPendingValidator] = useState(false);

  useEffect(() => {
    console.log("Provider:", provider);
    console.log("Account:", account);

    const fetchValidators = async () => {
      if (!provider) {
        console.log("No provider available");
        return;
      }
      try {
        const contract = await connectToContract(provider);
        const [addresses, stakes, rewards] = await contract.getValidators();
        const validatorData = addresses.map((addr, index) => ({
          index: index,
          address: addr,
          stake: formatEther(stakes[index] || 0),
          rewards: formatEther(rewards[index] || 0),
        }));
        setValidators(validatorData);
        // Reset isPendingValidator, pokud uživatel není validátor
        const isCurrentUserValidator = validatorData.some(
          (v) => v.address.toLowerCase() === account?.toLowerCase()
        );
        if (!isCurrentUserValidator) {
          setIsPendingValidator(false);
        }
      } catch (error) {
        console.error("Error fetching validators:", error);
      }
    };
    fetchValidators();
  }, [provider, account]);

  const handleFilterChange = (e) => {
    setFilterAddress(e.target.value);
  };

  const handleValidatorSearch = async () => {
    if (!provider || validatorNumber === "") return;
    try {
      const contract = await connectToContract(provider);
      const validatorIndex = parseInt(validatorNumber);
      const [addresses, stakes, rewards] = await contract.getValidators();
      if (validatorIndex >= 0 && validatorIndex < addresses.length) {
        setSelectedValidator({
          index: validatorIndex,
          address: addresses[validatorIndex],
          stake: formatEther(stakes[validatorIndex] || 0),
          rewards: formatEther(rewards[validatorIndex] || 0),
        });
      } else {
        alert("Validator not found!");
        setSelectedValidator(null);
      }
    } catch (error) {
      console.error("Error searching validator:", error);
      alert("Failed to fetch validator data!");
      setSelectedValidator(null);
    }
  };

  const handleWithdrawRewards = async () => {
    if (!provider || !account) {
      alert("Connect your wallet first!");
      return;
    }
    try {
      const contract = await connectToContract(provider);
      const tx = await contract.withdrawRewards();
      await tx.wait();
      alert("Validator rewards withdrawn successfully!");
      const [addresses, stakes, rewards] = await contract.getValidators();
      const validatorData = addresses.map((addr, index) => ({
        index: index,
        address: addr,
        stake: formatEther(stakes[index] || 0),
        rewards: formatEther(rewards[index] || 0),
      }));
      setValidators(validatorData);
      if (selectedValidator) {
        const updatedValidator = validatorData.find((v) => v.index === selectedValidator.index);
        setSelectedValidator(updatedValidator || null);
      }
    } catch (error) {
      console.error("Error withdrawing validator rewards:", error);
      alert(`Reward withdrawal failed: ${error.message}`);
    }
  };

  const handleLeaveValidator = async () => {
    if (!provider || !account) {
      alert("Connect your wallet first!");
      return;
    }
    try {
      const contract = await connectToContract(provider);
      const tx = await contract.removeValidator();
      await tx.wait();
      alert("Validator removed successfully!");
      const [addresses, stakes, rewards] = await contract.getValidators();
      const validatorData = addresses.map((addr, index) => ({
        index: index,
        address: addr,
        stake: formatEther(stakes[index] || 0),
        rewards: formatEther(rewards[index] || 0),
      }));
      setValidators(validatorData);
      setIsPendingValidator(false); // Reset stavu po odchodu
    } catch (error) {
      console.error("Error removing validator:", error);
      alert(`Failed to remove validator: ${error.message}`);
    }
  };

  const handleBecomeValidator = async () => {
    if (!provider || !account) {
      alert("Connect your wallet first!");
      return;
    }
    try {
      const contract = await connectToContract(provider);

      // Kontrola zůstatku
      const balance = await contract.balanceOf(account);
      const requiredAmount = parseEther("1000");
      if (balance < requiredAmount) {
        alert("Insufficient balance! You need at least 1000 CRW to become a validator.");
        return;
      }

      // Kontrola allowance
      const allowance = await contract.allowance(account, contractAddress);
      if (allowance < requiredAmount) {
        const approveTx = await contract.approve(contractAddress, requiredAmount);
        await approveTx.wait();
        alert("Approval for 1000 CRW successful!");
      }

      // Přidání validátora
      const tx = await contract.addValidator();
      await tx.wait();
      alert("Validator activated successfully!");

      // Nastavíme stav, aby tlačítko zmizelo hned po úspěšném přidání
      setIsPendingValidator(true);

      // Aktualizace seznamu validátorů
      const [addresses, stakes, rewards] = await contract.getValidators();
      const validatorData = addresses.map((addr, index) => ({
        index: index,
        address: addr,
        stake: formatEther(stakes[index] || 0),
        rewards: formatEther(rewards[index] || 0),
      }));
      setValidators(validatorData);
    } catch (error) {
      console.error("Error becoming validator:", error);
      alert(`Validator activation failed: ${error.message}`);
    }
  };

  const filteredValidators = filterAddress
    ? validators.filter((validator) =>
        validator.address.toLowerCase().includes(filterAddress.toLowerCase())
      )
    : validators;

  const isValidator = validators.some((v) => v.address.toLowerCase() === account?.toLowerCase());

  return (
    <div>
      <h2>
        <FaUserShield className="icon" /> Validators
      </h2>

      {/* Tlačítko zmizí, pokud je uživatel validátor nebo právě prošel procesem přidání */}
      {(!isValidator && !isPendingValidator && account) && (
        <div className="action-box">
          <h3>Become a Validator</h3>
          <p>Stake 1000 CRW to become a validator.</p>
          <button className="with-icon" onClick={handleBecomeValidator}>
            <FaPlus className="icon" /> Become Validator
          </button>
        </div>
      )}

      <div className="validator-search">
        <h3>Validator</h3>
        <input
          type="number"
          placeholder="Enter Validator Number"
          value={validatorNumber}
          onChange={(e) => setValidatorNumber(e.target.value)}
        />
        <button onClick={handleValidatorSearch}>Search Validator</button>

        {selectedValidator && (
          <div className="validator-details">
            <h4>Validator Details</h4>
            <table>
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Address</th>
                  <th>Stake (Crown)</th>
                  <th>Rewards (Crown)</th>
                  {selectedValidator.address.toLowerCase() === account?.toLowerCase() && (
                    <th>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{selectedValidator.index}</td>
                  <td>{selectedValidator.address}</td>
                  <td>{selectedValidator.stake}</td>
                  <td>{selectedValidator.rewards}</td>
                  {selectedValidator.address.toLowerCase() === account?.toLowerCase() && (
                    <td>
                      <button className="with-icon" onClick={handleWithdrawRewards}>
                        <FaGift className="icon" /> Withdraw Rewards
                      </button>
                      <button className="with-icon" onClick={handleLeaveValidator}>
                        <FaSignOutAlt className="icon" /> Leave Validator
                      </button>
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="validator-filter">
        <h3>Filter Validators by Address</h3>
        <input
          type="text"
          placeholder="Enter Address to Filter"
          value={filterAddress}
          onChange={handleFilterChange}
        />
      </div>

      <div className="validator-table">
        <h3>All Validators</h3>
        {filteredValidators.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Number</th>
                <th>Address</th>
                <th>Stake (Crown)</th>
                <th>Rewards (Crown)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredValidators.map((validator) => (
                <tr key={validator.index}>
                  <td>{validator.index}</td>
                  <td>{validator.address}</td>
                  <td>{validator.stake}</td>
                  <td>{validator.rewards}</td>
                  <td>
                    {validator.address.toLowerCase() === account?.toLowerCase() && (
                      <>
                        <button className="with-icon" onClick={handleWithdrawRewards}>
                          <FaGift className="icon" /> Withdraw Rewards
                        </button>
                        <button className="with-icon" onClick={handleLeaveValidator}>
                          <FaSignOutAlt className="icon" /> Leave Validator
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No validators found.</p>
        )}
      </div>
    </div>
  );
};

export default ValidatorList;